import {
    ChangeEvent,
    ClickEvent,
    DataDisplayEvent,
    DataSourceMode,
    Snackbar,
    Toast,
    ValidationResult
} from "@mcleod/components";
import {Api, CompanySettings, StringUtil} from "@mcleod/core";
import {CommonDialogs} from "@mcleod/common";
import {AutogenLayoutSmsWebControl} from "./autogen/AutogenLayoutSmsWebControl";


export class SmsWebControl extends AutogenLayoutSmsWebControl {

    async onLoad() {
        const companyType = CompanySettings.get().company_type;


        this.textboxWebUsername.addlValidationCallback = (_value: string, mode: DataSourceMode): ValidationResult => {
            return this.validateUserName(mode);
        };

        this.textboxWebPassword.addlValidationCallback = (_value: string, mode: DataSourceMode): ValidationResult => {
            return this.validateWebPassword(mode);
        }

        const isBroker = StringUtil.equalsIgnoreCase(companyType, "B");
        this.textboxCloser.visible = !isBroker;

        if (isBroker) {
            this.textboxNotificationMethod.tooltip = "Defines how the recipient is notified when a driver sends a text message";
        } else {
            this.textboxNotificationMethod.tooltip = "Defines how the recipient is notified when a driver or applicant sends a text message";
        }

        this.mainDataSource.doBeforeDataSourcePost = async () => {
            const changedData = this.mainDataSource?.data[0].getChangedData();
            // If specific fields have changed, prompt for confirmation
            if (this.updateApplicationFieldsChanged(changedData) && this.mainDataSource.mode === DataSourceMode.UPDATE) {
                try {
                    const userConfirmed = await this.confirmUpdateApplication();
                    if (!userConfirmed) {
                        return false;
                    }
                    return await this.updateApplication();

                } catch (error) {
                    console.error('Error during update confirmation:', error);
                    return false;
                }
            }
        }
    }

    textboxNotificationMethodOnDataDisplay(event: DataDisplayEvent) {
        if (this.sourceDispatchControl?.data[0]?.get("notification_method") === "R") {
            this.textboxEmail.visible = false;
        }

    }


    textboxNotificationMethodOnChange(event: ChangeEvent) {
        if (event.userInitiatedChange) {
            this.textboxEmail.visible = !StringUtil.equalsIgnoreCase(event.newValue, "Rapid alert");
        }
    }


    updateApplicationFieldsChanged(changedData: any) {
        return changedData["web_username"] != null || changedData["web_password"] != null || changedData["callback_url"] != null;
    }


    async initializeAccountOnClick(event: ClickEvent) {
        this.buttonInitializeAccount.busy = true;
        Api.post("lme/powerbroker/sms-initialize-account", {
            id: this.mainDataSource?.activeRow?.get("id"),
            application_id: this.textboxApplicationId.text,
            location_id: this.textboxLocationId.text,
            site_id: this.textboxSiteId.text,
            web_username: this.textboxWebUsername.text,
            web_password: this.textboxWebPassword.text,
            callback_url: this.textboxCallbackUrl.text,
            sender_id: this.textboxSenderId.text,
        })
            .then(async (response) => {
                if (response.data[0]?.success === true) {
                    this.textboxApplicationId.text = response.data[0]?.application_id;
                    this.textboxLocationId.text = response.data[0]?.location_id;
                    this.textboxSiteId.text = response.data[0]?.site_id;
                    this.textboxApplicationId.enabled = false;
                    this.textboxLocationId.enabled = false;
                    this.textboxSiteId.enabled = false;
                    this.textboxSiteId.required = true;
                    this.textboxApplicationId.required = true;
                    this.textboxLocationId.required = true;
                    this.buttonInitializeAccount.visible = false;
                    await this.mainDataSource.post();
                    setTimeout(() => Toast.showSuccessToast('Success!', `Account initialized`), 500);
                } else {
                    Snackbar.showWarningSnackbar(response.data[0].error);
                }
            })
            .catch((err) => {
                Snackbar.showWarningSnackbar(err.message);
            })
            .finally(() => {
                this.buttonInitializeAccount.busy = false;

            });
    }

    private async confirmUpdateApplication(): Promise<boolean> {
        return await CommonDialogs.showYesNo(
            `Are you sure you want to update the application?`,
            "Update Application"
        );
    }

    private async updateApplication(): Promise<boolean> {
        try {
            const response = await Api.post("lme/powerbroker/sms-update-application", {
                id: this.mainDataSource?.activeRow?.get("id"),
                url: this.mainDataSource?.activeRow?.get("callback_url"),
                username: this.mainDataSource?.activeRow?.get("account_username"),
                password: this.mainDataSource?.activeRow?.get("account_password"),
                webuser: this.mainDataSource?.activeRow?.get("web_username"),
                webpassword: this.mainDataSource?.activeRow?.get("web_password")

            });

            if (response.data[0]?.success === true) {
                setTimeout(() => Toast.showSuccessToast('Success!', `Application updated.`), 5000);
                return true;
            } else {
                Snackbar.showWarningSnackbar("Failed to update application callback URL.");
                return false;
            }
        } catch (err) {
            Snackbar.showWarningSnackbar("Failed to update application callback URL.");
            return false;
        }
    }

    userFieldsAreNotBlank() {
        return !StringUtil.isEmptyString(this.textboxCallbackUrl.text)
            && !StringUtil.isEmptyString(this.textboxWebUsername.text)
            && !StringUtil.isEmptyString(this.textboxWebPassword.text)
            && !StringUtil.isEmptyString(this.textboxSenderId.text);
    }

    setButtonIntitializeAccountVisibility() {
        if (this.userFieldsAreNotBlank()) {
            this.buttonInitializeAccount.visible = (StringUtil.isEmptyString(this.textboxApplicationId.text) || StringUtil.isEmptyString(this.textboxSiteId.text) || StringUtil.isEmptyString(this.textboxLocationId.text)) && !StringUtil.isEmptyString(this.textboxSenderId.text);
        } else {
            this.buttonInitializeAccount.visible = false;
        }
    }

    smsFieldsOnChange(event: ChangeEvent) {
        if (event.userInitiatedChange) {
            this.setButtonIntitializeAccountVisibility();
        }
    }

    textboxWebPasswordOnKeyDown(event: KeyboardEvent) {
        // if the user is typing the 64th character, prevent it
        if (this.textboxWebPassword.text.length >= 63 && event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
            this.textboxWebPassword.showAdhocTooltip("Web Password cannot exceed 63 characters." ,{shaking:true, timeout: 5000});
            event.preventDefault();
        }else{
            this.textboxWebPassword.tooltip = "Record this password somewhere safe since it will not be visible after saving this record.";
        }
    }

    smsControlOnDisplay(event: DataDisplayEvent) {
        this.setButtonIntitializeAccountVisibility();
        this.switchUpdateSchedCallTime.visible = this.switchCreateSmsCallin.checked;
    }


    private validateUserName(mode: DataSourceMode): ValidationResult {
        if (mode === DataSourceMode.SEARCH) {
            return null;
        }
        const result: ValidationResult = {component: this.textboxWebUsername, isValid: true};
        if (this.textboxWebUsername.text.length < 6) {
            result.isValid = false;
            result.validationMessage = "User name must be at least 6 characters long.";
        }
        return result;
    }

    switchCreateSmsCallinOnChange(event: ChangeEvent) {
        this.switchUpdateSchedCallTime.visible = this.switchCreateSmsCallin.checked;
    }

    private validateWebPassword(mode: DataSourceMode) {
        if (mode === DataSourceMode.SEARCH) {
            return null;
        }
        const result: ValidationResult = {component: this.textboxWebPassword, isValid: true};
        if (this.textboxWebPassword.text.length > 63) {
            result.isValid = false;
            result.validationMessage = "Web Password cannot exceed 63 characters.";
        }
        return result;
    }
}
